import { makeStyles, MessageBar, MessageBarBody, tokens } from '@fluentui/react-components';
import { ColumnType, KanbanDateLaneOrder, type TableRecord } from '@powerportalspro/core';
import { AuthStatus, useAuth, useT } from '@powerportalspro/react';
import { KanbanBoard, type ViewDefinition } from '@powerportalspro/react-fluent';
import { useMemo } from 'react';
import { DemoShowcaseLayout } from './DemoShowcaseLayout';
import { companiesIn, MIDWEST, NORTHEAST, SOUTHEAST, SOUTHWEST, WEST, type DemoRegion } from './DemoRegion';

// The open tasks of the demo's sample data, and a signed-in visitor's own, in lanes of when they're
// due: Overdue, Today, Tomorrow, This week, Next week, Later and No date, in the visitor's time zone.
// Each lane is in due-date order. The sample tasks stay where they are: visitors can't change them. A
// signed-in visitor adds up to five tasks of their own with a lane's Add button, each a high priority
// to start with, and moves them for real: a move changes the task's due date. A view for each region of
// the country narrows the board to the tasks about companies there.

const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Demos.KanbanBoardShowcase.KanbanBoardDueDatesMainDemo';

const OPEN_TASKS_VIEW_ID = '7d2a3c41-8e5f-4b6a-9c1d-3e0f5a7b2c91';

// A view of the open tasks about each region's companies, beside the view of them all.
const REGION_VIEWS: { region: DemoRegion; viewId: string }[] = [
  { region: WEST, viewId: '37ad6cbc-26d3-4eba-b8dc-89c79d84bb87' },
  { region: SOUTHWEST, viewId: 'a5a1df04-4f8d-440a-869b-008af7d5b53b' },
  { region: MIDWEST, viewId: '9e5d06df-1f2e-4a57-b2c1-5eb3a5fa7d34' },
  { region: SOUTHEAST, viewId: '8fbffb5c-d0e4-4682-8495-cad42b9686af' },
  { region: NORTHEAST, viewId: '3b64b0d2-b65b-4599-97c2-f2d6a4199084' },
];

// A task's High priority.
const HIGH_PRIORITY = 2;

// The open tasks — the sample data's, and a signed-in visitor's own, which the portal's table access
// adds — or those about a region's companies, with their subject, priority and what they're about on
// the cards.
function openTasksView(id: string, displayName: string, region: DemoRegion | null): ViewDefinition {
  return {
    id,
    tableName: 'task',
    displayName,
    pageSize: 10,
    fetchXml: `
      <fetch>
        <entity name="task">
          <attribute name="subject" />
          <attribute name="scheduledend" />
          <attribute name="prioritycode" />
          <attribute name="regardingobjectid" />
          <order attribute="scheduledend" />
          <filter>
            <condition attribute="statecode" operator="eq" value="0" />
          </filter>
          ${region ? companiesIn(region, 'regardingobjectid') : ''}
        </entity>
      </fetch>`,
    columns: [
      { columnName: 'subject' },
      { columnName: 'scheduledend' },
      { columnName: 'prioritycode' },
      { columnName: 'regardingobjectid' },
    ],
  };
}

// Every task added on the board starts with a high priority, beside the due date of its lane.
function highPriority(record: TableRecord): TableRecord {
  return {
    ...record,
    properties: { ...record.properties, prioritycode: { $type: ColumnType.Choice, value: HIGH_PRIORITY } },
  };
}

const useStyles = makeStyles({
  list: {
    margin: 0,
    paddingInlineStart: tokens.spacingHorizontalXL,
  },
});

// The strings are HTML, written once for both stacks.
function Item({ text }: { text: string }) {
  return <li dangerouslySetInnerHTML={{ __html: text }} />;
}

export function KanbanBoardDueDatesMainSample() {
  const styles = useStyles();
  const t = useT();
  const auth = useAuth();
  const signedIn = auth.status === AuthStatus.Authenticated;

  // Built once, as a new list starts the board again.
  const views = useMemo(
    () => [
      openTasksView(OPEN_TASKS_VIEW_ID, t(`${STRINGS_BASE}.view-open-tasks`), null),
      ...REGION_VIEWS.map(({ region, viewId }) => openTasksView(viewId, t(`${STRINGS_BASE}.view-${region.key}`), region)),
    ],
    [t],
  );

  return (
    <DemoShowcaseLayout
      title={t(`${STRINGS_BASE}.try-title`)}
      tips={
        <>
          <MessageBar intent="info">
            <MessageBarBody>{t(`${STRINGS_BASE}.${signedIn ? 'moves-saved' : 'sign-in-to-add'}`)}</MessageBarBody>
          </MessageBar>

          <ul className={styles.list}>
            <Item text={t(`${STRINGS_BASE}.try-lanes`)} />
            <Item text={t(`${STRINGS_BASE}.try-add`)} />
            <Item text={t(`${STRINGS_BASE}.try-move`)} />
            <Item text={t(`${STRINGS_BASE}.try-order`)} />
            <Item text={t(`${STRINGS_BASE}.try-no-date`)} />
            <Item text={t(`${STRINGS_BASE}.try-overdue`)} />
            <Item text={t(`${STRINGS_BASE}.try-regions`)} />
          </ul>
        </>
      }
    >
      <KanbanBoard
        customViewDefinitions={views}
        defaultViewId={OPEN_TASKS_VIEW_ID}
        laneColumn="scheduledend"
        dateLaneOrder={KanbanDateLaneOrder.Date}
        editable
        allowFiltering
        prepareNewRecord={highPriority}
        persistedStateQueryParameter="boardState"
        height="760px"
      />
    </DemoShowcaseLayout>
  );
}
